/** Inbound events and the exception queue (M15-40, M15-43). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { ReasonDialog } from '../../components/ReasonDialog';
import { useEventTypes } from './EventTypesPage';
import { EVENT_TONE, JsonBlock, errorText } from './shared';

interface EventRow { id: string; eventType: string; schemaVersion: number; externalSystem: string; externalRef: string; businessDate: string; branch: string | null; status: 'RECEIVED' | 'POSTED' | 'EXCEPTION' | 'IGNORED'; exceptionCode: string | null; exceptionReason: string | null; journalEntryId: string | null; journalNumber: string | null; attempts: number; receivedAt: string; processedAt: string | null }
interface Summary { eventType: string; businessDate: string; branch: string | null; events: number }

function EventDetail({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const q = useQuery({ queryKey: ['event', id], queryFn: () => api<EventRow & { payload: unknown }>(`/events/${id}`) });
  return (
    <Modal title={q.data ? `${q.data.externalSystem} · ${q.data.externalRef}` : 'Event'} description={q.data ? `${q.data.eventType} v${q.data.schemaVersion} · received ${dateTime(q.data.receivedAt)}` : undefined} onClose={onClose}>
      {q.isLoading ? <Skeleton className="h-40 w-full" /> : q.data && (
        <div className="space-y-3">
          {q.data.exceptionReason && <Alert tone="critical"><code className="text-xs">{q.data.exceptionCode}</code> {q.data.exceptionReason}</Alert>}
          {q.data.journalEntryId && <p className="text-sm">Posted as <Link className="font-semibold text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${q.data.journalEntryId}`}>{q.data.journalNumber}</Link></p>}
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payload as received (never changed)</p>
          <JsonBlock value={q.data.payload} />
        </div>
      )}
    </Modal>
  );
}

export function EventsPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'EXCEPTION';
  const [eventType, setEventType] = useState('');
  const [ref, setRef] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [viewing, setViewing] = useState<string | null>(null);
  const [ignoring, setIgnoring] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical' | 'warning'; text: string } | null>(null);
  const types = useEventTypes();
  const qs = new URLSearchParams({ page: String(page), pageSize: '50', ...(status ? { status } : {}), ...(eventType ? { eventType } : {}), ...(ref ? { externalRef: ref } : {}), ...(from ? { dateFrom: from } : {}), ...(to ? { dateTo: to } : {}) });
  const list = useQuery({ queryKey: ['events', qs.toString()], queryFn: () => api<{ rows: EventRow[]; total: number; pageSize: number }>(`/events?${qs}`) });
  const summaries = useQuery({ queryKey: ['event-summaries'], queryFn: () => api<Summary[]>('/events/summaries') });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['events'] }); void qc.invalidateQueries({ queryKey: ['event-summaries'] }); };

  const reprocess = useMutation({
    mutationFn: (ids: string[]) => api<{ results: { id: string; status: string; exceptionCode: string | null }[] }>('/events/exceptions/reprocess', { method: 'POST', body: { ids } }),
    onSuccess: (r) => {
      refresh();
      setPicked([]);
      const posted = r.results.filter((x) => x.status === 'POSTED').length;
      const waiting = r.results.filter((x) => x.status === 'RECEIVED').length;
      const still = r.results.length - posted - waiting;
      setMessage({ tone: still ? 'warning' : 'ok', text: `${posted} posted${waiting ? `, ${waiting} back in the daily pool` : ''}${still ? `, ${still} still held — check the reasons` : ''}.` });
    },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });
  const ignore = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api(`/events/${id}/ignore`, { method: 'POST', body: { reason } }),
    onSuccess: () => { refresh(); setIgnoring(null); },
  });
  const summarize = useMutation({
    mutationFn: (s: Summary) => api<{ posted: number; held: number; reason?: string }>('/events/summaries', { method: 'POST', body: { eventType: s.eventType, businessDate: s.businessDate, ...(s.branch ? { branch: s.branch } : {}) } }),
    onSuccess: (r) => { refresh(); setMessage({ tone: r.held ? 'warning' : 'ok', text: `Summary posted for ${r.posted} event${r.posted === 1 ? '' : 's'}${r.held ? `; ${r.held} held in exceptions${r.reason ? `: ${r.reason}` : ''}` : ''}.` }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });

  const rows = list.data?.rows ?? [];
  const selectable = rows.filter((r) => r.status === 'EXCEPTION');
  const setStatus = (s: string) => { setParams(s ? { status: s } : {}); setPage(1); setPicked([]); };

  return (
    <>
      <PageHeader
        title="Events & Exceptions"
        description="Everything outside systems sent. Held events keep their reason; fix the rule or mapping, then reprocess — posting twice is impossible."
        actions={can('exceptions.manage') && picked.length > 0 && <Button busy={reprocess.isPending} onClick={() => reprocess.mutate(picked)}><RefreshCw aria-hidden="true" className="h-4 w-4" /> Reprocess {picked.length}</Button>}
      />
      <FilterBar>
        <select className={filterClass} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="EXCEPTION">Exceptions</option>
          <option value="RECEIVED">Waiting (daily summary)</option>
          <option value="POSTED">Posted</option>
          <option value="IGNORED">Ignored</option>
          <option value="">All</option>
        </select>
        <select className={filterClass} value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(1); }} aria-label="Event type">
          <option value="">All event types</option>
          {types.data?.map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
        </select>
        <input className={filterClass} placeholder="External ref" value={ref} onChange={(e) => { setRef(e.target.value); setPage(1); }} aria-label="External reference" />
        <input className={filterClass} type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label="From" />
        <input className={filterClass} type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label="To" />
      </FilterBar>
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}

      {(summaries.data?.length ?? 0) > 0 && (
        <Card title="Daily summaries waiting" description="Events of daily-summary types post together, one journal per day and branch." className="mb-4">
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {summaries.data!.map((s) => (
              <li key={`${s.eventType}|${s.businessDate}|${s.branch}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span><code className="text-xs">{s.eventType}</code> · {shortDate(s.businessDate)}{s.branch ? ` · ${s.branch}` : ''} · {s.events} event{s.events === 1 ? '' : 's'}</span>
                {can('exceptions.manage', 'events.submit') && <Button size="sm" busy={summarize.isPending} onClick={() => summarize.mutate(s)}>Post summary</Button>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        {list.isLoading ? <Skeleton className="h-40 w-full" /> : rows.length === 0 ? (
          <EmptyState title={status === 'EXCEPTION' ? 'No exceptions' : 'No events'} description={status === 'EXCEPTION' ? 'Every event received so far has posted.' : 'Nothing matches these filters.'} />
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  {can('exceptions.manage') && selectable.length > 0 && (
                    <Th><input type="checkbox" aria-label="Select all" checked={picked.length === selectable.length} onChange={(e) => setPicked(e.target.checked ? selectable.map((r) => r.id) : [])} /></Th>
                  )}
                  <Th>Source</Th><Th>Event</Th><Th>Date</Th><Th>Status</Th><Th>Reason / journal</Th><Th> </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.id}>
                    {can('exceptions.manage') && selectable.length > 0 && (
                      <Td>{r.status === 'EXCEPTION' && <input type="checkbox" aria-label={`Select ${r.externalRef}`} checked={picked.includes(r.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))} />}</Td>
                    )}
                    <Td><button className="text-left font-medium text-brand-700 hover:underline dark:text-brand-100" onClick={() => setViewing(r.id)}>{r.externalRef}</button><span className="block text-xs text-slate-500">{r.externalSystem}</span></Td>
                    <Td><code className="text-xs">{r.eventType}</code>{r.branch && <span className="block text-xs text-slate-500">{r.branch}</span>}</Td>
                    <Td className="whitespace-nowrap">{shortDate(r.businessDate)}</Td>
                    <Td><StatusChip tone={EVENT_TONE[r.status]}>{r.status.toLowerCase()}</StatusChip>{r.attempts > 1 && <span className="block text-xs text-slate-500">{r.attempts} attempts</span>}</Td>
                    <Td className="max-w-md text-sm">
                      {r.journalEntryId ? <Link className="font-medium text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${r.journalEntryId}`}>{r.journalNumber}</Link> : r.exceptionReason ? <><code className="text-xs text-red-700 dark:text-red-300">{r.exceptionCode}</code><span className="block text-xs text-slate-600 dark:text-slate-300">{r.exceptionReason}</span></> : <span className="text-slate-500">—</span>}
                    </Td>
                    <Td>
                      {r.status === 'EXCEPTION' && can('exceptions.manage') && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="secondary" busy={reprocess.isPending && reprocess.variables?.includes(r.id)} onClick={() => reprocess.mutate([r.id])}>Reprocess</Button>
                          <Button size="sm" variant="ghost" onClick={() => setIgnoring(r.id)}>Ignore</Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {list.data && list.data.total > list.data.pageSize && (
              <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                <Button size="sm" variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <span className="text-slate-500">Page {page} of {Math.ceil(list.data.total / list.data.pageSize)}</span>
                <Button size="sm" variant="secondary" disabled={page * list.data.pageSize >= list.data.total} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Card>
      {viewing && <EventDetail id={viewing} onClose={() => setViewing(null)} />}
      {ignoring && <ReasonDialog title="Ignore this event?" description="It stays on record with your reason and is never posted." confirmLabel="Ignore" busy={ignore.isPending} error={ignore.error ? errorText(ignore.error) : null} onClose={() => setIgnoring(null)} onConfirm={(reason) => ignore.mutate({ id: ignoring, reason })} />}
    </>
  );
}
